import axios from "../utils/axios.wrapper";

// Get all orders
export const getAllOrders = async () => {
  const { data } = await axios.get("/order");
  return data;
};

// Get active orders
export const getActiveOrders = async () => {
  const { data } = await axios.get("/order/active");
  return data.items || data;
};

// Get inactive orders
export const getInactiveOrders = async () => {
  const { data } = await axios.get("/order/inactive");
  return data.items || data;
};

// Get order by ID (includes orderLines and payments)
export const getOrderById = async (id) => {
  const { data } = await axios.get(`/order/${id}`);
  return data;
};

// Get orders by shop
export const getOrdersByShop = async (shopId) => {
  const { data } = await axios.get(`/order/shop/${shopId}`);
  return data.items || data;
};

// Get orders by customer (for customer orders - orderTypeId: 0)
export const getOrdersByCustomer = async (customerId) => {
  const { data } = await axios.get(`/order/customer/${customerId}`);
  return data.items || data;
};

// Get orders by lab (for shop orders - orderTypeId: 1)
export const getOrdersByLab = async (labId) => {
  const { data } = await axios.get(`/order/lab/${labId}`);
  return data.items || data;
};

// Get orders by type (0=customer order, 1=shop order)
export const getOrdersByType = async (typeId) => {
  const { data } = await axios.get(`/order/type/${typeId}`);
  return data.items || data;
};

// Get orders by status
export const getOrdersByStatus = async (statusId) => {
  const { data } = await axios.get(`/order/status/${statusId}`);
  return data.items || data;
};

// Create new order
// orderData includes: order info + orderLines array + payments array (for customer orders)
export const createOrder = async (orderData) => {
  const { data } = await axios.post("/order/create", orderData);
  return data;
};

// Update order
// orderData includes: order info + orderLines array + payments array (for customer orders)
export const updateOrder = async (id, orderData) => {
  const { data } = await axios.put(`/order/${id}`, orderData);
  return data;
};

// Delete order
export const deleteOrder = async (id) => {
  const { data } = await axios.delete(`/order/${id}`);
  return data;
};

// Cancel order
export const cancelOrder = async (id) => {
  const { data } = await axios.delete(`/order/cancel/${id}`);
  return data;
};

// Transform order to sale
export const transformOrderToSale = async (id) => {
  const { data } = await axios.post(`/order/${id}/to-sale`);
  return data;
};

// Search orders
export const searchOrders = async (query) => {
  const { data } = await axios.get(`/order/search?q=${encodeURIComponent(query)}`);
  return data.items || data;
};

// Get orders with filters (date range, status, shop, etc.)
export const getOrdersWithFilters = async (filters) => {
  const params = new URLSearchParams(filters).toString();
  const { data } = await axios.get(`/order?${params}`);
  return data.items || data;
};

// Générer la commande boutique pour demain
export const generateTomorrowShopOrders = async (shopId) => {
  const { data } = await axios.post(`/order/shops/${shopId}/generate-tomorrow`);
  return data;
};

// Get customer order by order creation date
export const getOrdersByDateRange = async (filter) => {
  const response = await axios.post('order/daterange', filter);
  const data = response.data;

  return {
    items: data?.items ?? data ?? [],
    totalCount: data?.totalCount ?? data ?? 0,
  };
};

export const getOrdersByDeliveryDateRange = async (filter) => {
  const response = await axios.post('order/deliverydaterange', filter);
  const data = response.data;

  return {
    items: data?.items ?? data ?? [],
    totalCount: data?.totalCount ?? data ?? 0,
  };
};

export const getShopOrdersByDateRange = async (filter) => {
  const response = await axios.post('order/shop/daterange', filter);
  const data = response.data;

  return {
    items: data?.items ?? data ?? [],
    totalCount: data?.totalCount ?? data ?? 0,
  };
};

export const getShopOrdersByDeliveryDateRange = async (filter) => {
  const response = await axios.post('order/shop/deliverydaterange', filter);
  const data = response.data;

  return {
    items: data?.items ?? data ?? [],
    totalCount: data?.totalCount ?? data ?? 0,
  };
};


// Générer la commande boutique pour une date donnée
export const generateShopOrderByDate = async (shopId, orderDate) => {
  const { data } = await axios.post(
    `/order/shops/${shopId}/generate-shop-order-by-date`,
    { orderDate }
  );
  return data;
};

export const updateOrderAsync = async (id, orderData) => {
  const { data } = await axios.put(`/order/${id}`, orderData);
  return data; // { jobId, orderId, ... }
};

export const generateTomorrowShopOrdersAsync = async (shopId) => {
  const { data } = await axios.post(`/order/shops/${shopId}/generate-tomorrow`);
  return data; // { jobId, shopId, ... }
};

export const generateShopOrderByDateAsync = async (shopId, orderDate) => {
  const { data } = await axios.post(
    `/order/shops/${shopId}/generate-shop-order-by-date`,
    { orderDate }
  );
  return data; // { jobId, shopId, orderDate, ... }
};

const generateShopOrderAnnexByDate = async (shopId, orderDate, forceEmptyAnnex = false) => {
  const response = await axios.post(
    `/order/shops/${shopId}/annex/generate-by-date`,
    {
      orderDate,
      forceEmptyAnnex,
    }
  );

  return response.data;
};

export const generateShopOrderAnnexByDateAsync = async (shopId, orderDate) => {
  const { data } = await axios.post(
    `/order/shops/${shopId}/annex/generate-by-date`,
    null,
    {
      params: { orderDate },
    }
  );
  return data; // si backend renvoie directement l'annexe, pas forcément de jobId
};

export const getLabProductionRange = async (filter) => {
  const response = await axios.post('/order/lab-production-range', filter);
  const data = response.data;

  return {
    items: data?.items ?? [],
    totalCount: data?.totalCount ?? 0,
  };
};

export const pullOrdersFromSite = async (filter = {}) => {
  const { data } = await axios.post('/externalshopsync/pull-orders', filter);
  return data;
};

export const orderService = {
  getAllOrders,
  getActiveOrders,
  getInactiveOrders,
  getOrderById,
  getOrdersByShop,
  getOrdersByCustomer,
  getOrdersByLab,
  getOrdersByType,
  getOrdersByStatus,
  createOrder,
  updateOrder,
  updateOrderAsync,
  deleteOrder,
  cancelOrder,
  transformOrderToSale,
  searchOrders,
  getOrdersWithFilters,
  generateTomorrowShopOrders,
  generateTomorrowShopOrdersAsync,
  getOrdersByDateRange,
  getOrdersByDeliveryDateRange,
  getShopOrdersByDateRange,
  getShopOrdersByDeliveryDateRange,
  generateShopOrderByDate,
  generateShopOrderByDateAsync,
  generateShopOrderAnnexByDate,
  generateShopOrderAnnexByDateAsync,
  getLabProductionRange,
  pullOrdersFromSite,
};